// =====================================================================
// «VERIFICA TU EMPRESA» · ESTABILIDAD Parte C (F5)
// =====================================================================
// El asistente que ve el Admin Supremo de una empresa nueva cuando la
// identidad verificable está encendida (y solo entonces: con el aviso de
// privacidad en borrador el servidor dice que está apagada y esta pantalla
// no aparece nunca). Un dato por pantalla, con el avance a la vista:
//
//   1 · Qué es y qué necesitas       2 · Tu teléfono (código por Telegram)
//   3 · RFC y nombre fiscal          4 · Tu CURP (y tu nombre como en ella)
//   5 · Tu Constancia de Situación Fiscal (PDF)
//
// Se retoma donde se quedó: si ya verificó el teléfono, abre en el RFC.
// El servidor es quien decide; aquí se acompaña, se valida mientras se
// escribe y se dice qué hacer cuando algo no cuadra.
import { useState } from 'react';
import { usePaso } from '../ui/navegacionReact';
import {
  BadgeCheck, Building2, CheckCircle2, FileUp, IdCard, Loader2, LogOut,
  MessageCircle, Send, ShieldCheck, Smartphone,
} from 'lucide-react';
import { api } from '../api';
import Ayuda from '../ui/Ayuda';
import {
  PASOS, normalizarClave, primerPasoPendiente, problemaCurp, problemaRfc,
  problemaTelefono, soloDigitos, telefonoDiez,
} from './identidad';

const BOTON = 'inline-flex min-h-11 items-center justify-center gap-2 rounded-lg '
  + 'bg-[var(--color-marca)] px-4 py-2 text-sm font-semibold text-white disabled:opacity-60';
const CAMPO = 'w-full rounded-lg border border-gray-300 px-3 py-3 text-base';

function Avance({ paso }) {
  return (
    <div>
      <p className="text-sm font-medium text-gray-700" aria-live="polite">
        Paso {paso + 1} de {PASOS.length}: {PASOS[paso]}
      </p>
      <ol className="mt-2 grid grid-cols-5 gap-1.5" aria-hidden="true">
        {PASOS.map((p, i) => (
          <li key={p} className={`h-1.5 rounded-full ${i <= paso ? 'bg-[var(--color-marca)]' : 'bg-gray-200'}`} />
        ))}
      </ol>
    </div>
  );
}

function Etiqueta({ htmlFor, children }) {
  return (
    <label htmlFor={htmlFor} className="block text-sm font-medium text-gray-800">
      {children} <span className="text-red-600" aria-hidden="true">*</span>
    </label>
  );
}

// Lo que falta, dicho debajo del botón apagado (criterio 8).
function Motivo({ children }) {
  if (!children) return null;
  return <p className="mt-1 text-xs text-gray-600" role="status">{children}</p>;
}

export default function AsistenteIdentidad({ estadoInicial, onTerminar, onSalir }) {
  const pasos = estadoInicial?.pasos || {};
  const [paso, setPaso] = useState(estadoInicial ? primerPasoPendiente(estadoInicial) : 0);
  const [empezado, setEmpezado] = useState(false);
  const [cargando, setCargando] = useState(false);
  const [error, setError] = useState(null);
  const [aviso, setAviso] = useState(null);

  const [telefono, setTelefono] = useState(pasos.telefono?.sugerido || '');
  const [envio, setEnvio] = useState(null);
  const [codigo, setCodigo] = useState('');
  const [rfc, setRfc] = useState('');
  const [razon, setRazon] = useState(pasos.rfc?.razon_social || '');
  const [curp, setCurp] = useState('');
  const [nombre, setNombre] = useState(pasos.curp?.nombre || '');
  const [archivo, setArchivo] = useState(null);
  const [terminado, setTerminado] = useState(false);

  // El primer paso («Qué es») se ve siempre al abrir; luego salta a lo que falta.
  const visible = empezado ? paso : 0;
  usePaso('identidad', visible, setPaso);          // atrás = el paso anterior, igual que el botón de abajo

  async function correr(fn) {
    setCargando(true);
    setError(null);
    setAviso(null);
    try {
      await fn();
    } catch (e) {
      setError(e.message || 'No se pudo completar. Revisa la conexión y vuelve a intentarlo.');
    } finally {
      setCargando(false);
    }
  }

  const alCompletar = (r) => { if (r && r.completa) setTerminado(true); };

  const mandarCodigo = () => correr(async () => {
    const r = await api.post('/seguridad/identidad/telefono/codigo', { telefono: telefonoDiez(telefono) });
    setEnvio(r);
    setAviso(r.mensaje);
  });
  const comprobarCodigo = () => correr(async () => {
    const r = await api.post('/seguridad/identidad/telefono/verificar', { codigo });
    alCompletar(r);
    setAviso(r.mensaje);
    setPaso(2);
  });
  const guardarRfc = () => correr(async () => {
    const r = await api.put('/seguridad/identidad/empresa/rfc', {
      rfc: normalizarClave(rfc), razon_social: razon.trim(),
    });
    alCompletar(r);
    setAviso(`Guardado: ${r.mascara}. En pantalla solo se ve el principio.`);
    setPaso(3);
  });
  const guardarCurp = () => correr(async () => {
    const r = await api.put('/seguridad/identidad/curp', {
      curp: normalizarClave(curp), nombre_completo: nombre.trim() || null,
    });
    alCompletar(r);
    setAviso(`Guardada: ${r.mascara}.`);
    setPaso(4);
  });
  const subirConstancia = () => correr(async () => {
    const r = await api.subir('/seguridad/identidad/csf', archivo);
    alCompletar(r);
    if (!r.completa) setAviso('Constancia aceptada. Falta completar algún paso anterior: revisa los que no tienen palomita.');
  });

  const motivoTelefono = problemaTelefono(telefono);
  const motivoCodigo = soloDigitos(codigo).length === 6 ? null : 'Se enciende cuando escribas los 6 dígitos.';
  const motivoRfc = problemaRfc(rfc) || (razon.trim().length < 2 ? 'Falta el nombre fiscal, como viene en tu Constancia.' : null);
  const motivoCurp = problemaCurp(curp) || (nombre.trim().split(/\s+/).length < 2 ? 'Falta tu nombre con tus apellidos.' : null);
  const motivoArchivo = !archivo ? 'Elige el PDF de tu Constancia.'
    : (archivo.type && archivo.type !== 'application/pdf' ? 'Ese archivo no es un PDF. Sube el PDF que descargas del SAT.' : null);

  if (terminado) {
    return (
      <Marco onSalir={onSalir}>
        <p className="flex items-center gap-2 text-base font-semibold text-green-800">
          <CheckCircle2 className="h-6 w-6" aria-hidden="true" /> Listo: tu empresa quedó verificada.
        </p>
        <p className="text-sm text-gray-700">
          Tu teléfono, el RFC, tu CURP y tu Constancia coinciden. Si un día alguien te quita la
          cuenta, con estos datos se comprueba que eres tú para devolvértela.
        </p>
        <button type="button" className={BOTON} onClick={onTerminar}>Entrar a TEGO</button>
      </Marco>
    );
  }

  return (
    <Marco onSalir={onSalir}>
      <Avance paso={visible} />

      {visible === 0 && (
        <div className="space-y-3 text-sm text-gray-700">
          <p>Son cuatro datos, uno por pantalla. Tarda unos cinco minutos. Ten a la mano:</p>
          <ul className="space-y-2">
            <li className="flex gap-2"><Smartphone className="h-5 w-5 shrink-0" aria-hidden="true" />
              Tu teléfono con Telegram, para recibir un código de 6 dígitos.</li>
            <li className="flex gap-2"><Building2 className="h-5 w-5 shrink-0" aria-hidden="true" />
              El RFC de tu empresa (la clave que te dio el SAT).</li>
            <li className="flex gap-2"><IdCard className="h-5 w-5 shrink-0" aria-hidden="true" />
              Tu CURP (tu Clave Única de Registro de Población; viene en tu credencial para votar).</li>
            <li className="flex gap-2"><FileUp className="h-5 w-5 shrink-0" aria-hidden="true" />
              Tu Constancia de Situación Fiscal en PDF, de 30 días o menos. Se descarga gratis en el portal del SAT.</li>
          </ul>
          <Ayuda id="seguridad.identidad.privacidad" />
          <button type="button" className={BOTON} onClick={() => setEmpezado(true)}>
            <ShieldCheck className="h-4 w-4" aria-hidden="true" /> Empezar
          </button>
        </div>
      )}

      {visible === 1 && (
        <div className="space-y-3">
          {!envio && (
            <>
              <Etiqueta htmlFor="id-telefono">Tu teléfono</Etiqueta>
              <input id="id-telefono" type="tel" inputMode="tel" autoComplete="tel" autoFocus
                     value={telefono} onChange={(e) => setTelefono(e.target.value)}
                     placeholder="81 1234 5678" className={CAMPO} />
              <p className="text-xs text-gray-600">Los 10 dígitos, con la clave de tu ciudad. Puedes escribirlo con espacios o guiones.</p>
              <button type="button" className={BOTON} disabled={Boolean(motivoTelefono) || cargando} onClick={mandarCodigo}>
                {cargando ? <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" /> : <Send className="h-4 w-4" aria-hidden="true" />}
                Mandar código
              </button>
              <Motivo>{telefono ? motivoTelefono : 'Escribe tu teléfono para mandarte el código.'}</Motivo>
            </>
          )}
          {envio && (
            <>
              {envio.via === 'ENLACE' && (
                <a href={envio.enlace} target="_blank" rel="noreferrer"
                   className="inline-flex min-h-11 items-center gap-2 rounded-lg border border-gray-300 px-4 py-2 text-sm font-semibold">
                  <MessageCircle className="h-4 w-4" aria-hidden="true" /> Abrir Telegram para recibir el código
                </a>
              )}
              <Etiqueta htmlFor="id-codigo">Código de 6 dígitos</Etiqueta>
              <input id="id-codigo" inputMode="numeric" autoComplete="one-time-code"
                     value={codigo} onChange={(e) => setCodigo(soloDigitos(e.target.value))}
                     placeholder="123456"
                     className="w-full rounded-lg border border-gray-300 px-3 py-3 text-center font-mono text-2xl tracking-[0.4em]" />
              <Ayuda id="seguridad.identidad.telefono" />
              <div className="flex flex-wrap gap-2">
                <button type="button" className={BOTON} disabled={Boolean(motivoCodigo) || cargando} onClick={comprobarCodigo}>
                  {cargando && <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />}
                  <BadgeCheck className="h-4 w-4" aria-hidden="true" /> Comprobar código
                </button>
                <button type="button" onClick={() => { setEnvio(null); setCodigo(''); }}
                        className="min-h-11 rounded-lg px-3 py-2 text-sm text-gray-700 hover:bg-gray-100">
                  Cambiar teléfono o pedir otro código
                </button>
              </div>
              <Motivo>{motivoCodigo}</Motivo>
            </>
          )}
        </div>
      )}

      {visible === 2 && (
        <div className="space-y-3">
          <Etiqueta htmlFor="id-rfc">RFC de tu empresa</Etiqueta>
          <input id="id-rfc" autoFocus value={rfc} onChange={(e) => setRfc(normalizarClave(e.target.value).slice(0, 13))}
                 placeholder="ABC010203XY1" className={`${CAMPO} font-mono uppercase`} />
          <Ayuda id="seguridad.identidad.rfc" />
          <Etiqueta htmlFor="id-razon">Nombre fiscal</Etiqueta>
          <input id="id-razon" value={razon} onChange={(e) => setRazon(e.target.value)}
                 placeholder="BODEGAS DEL NORTE" className={CAMPO} />
          <Ayuda id="seguridad.identidad.razon_social" />
          <button type="button" className={BOTON} disabled={Boolean(motivoRfc) || cargando} onClick={guardarRfc}>
            {cargando && <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />} Guardar y seguir
          </button>
          <Motivo>{rfc || razon ? motivoRfc : 'Escribe el RFC y el nombre fiscal tal como vienen en tu Constancia.'}</Motivo>
        </div>
      )}

      {visible === 3 && (
        <div className="space-y-3">
          <Etiqueta htmlFor="id-curp">Tu CURP</Etiqueta>
          <input id="id-curp" autoFocus value={curp} onChange={(e) => setCurp(normalizarClave(e.target.value).slice(0, 18))}
                 placeholder="AAAA000000HDFBBB09" className={`${CAMPO} font-mono uppercase`} />
          <Ayuda id="seguridad.identidad.curp" />
          <Etiqueta htmlFor="id-nombre">Tu nombre como en tu CURP</Etiqueta>
          <input id="id-nombre" value={nombre} onChange={(e) => setNombre(e.target.value)} className={CAMPO} />
          <Ayuda id="seguridad.identidad.nombre" />
          <button type="button" className={BOTON} disabled={Boolean(motivoCurp) || cargando} onClick={guardarCurp}>
            {cargando && <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />} Guardar y seguir
          </button>
          <Motivo>{curp ? motivoCurp : 'Escribe tu CURP de 18 caracteres.'}</Motivo>
        </div>
      )}

      {visible === 4 && (
        <div className="space-y-3">
          <Etiqueta htmlFor="id-csf">Tu Constancia de Situación Fiscal (PDF)</Etiqueta>
          <input id="id-csf" type="file" accept="application/pdf,.pdf"
                 onChange={(e) => setArchivo(e.target.files?.[0] || null)}
                 className="block w-full text-sm file:mr-3 file:min-h-11 file:rounded-lg file:border-0 file:bg-gray-100 file:px-4 file:py-2" />
          <Ayuda id="seguridad.identidad.csf" />
          {pasos.csf?.ultimo_rechazo && !error && (
            <p className="rounded-lg border border-amber-200 bg-amber-50 p-2.5 text-sm text-amber-900">
              La última Constancia no se aceptó: {pasos.csf.ultimo_rechazo}
            </p>
          )}
          <button type="button" className={BOTON} disabled={Boolean(motivoArchivo) || cargando} onClick={subirConstancia}>
            {cargando ? <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" /> : <FileUp className="h-4 w-4" aria-hidden="true" />}
            {cargando ? 'Revisando tu Constancia…' : 'Subir Constancia'}
          </button>
          <Motivo>{motivoArchivo}</Motivo>
        </div>
      )}

      {aviso && !error && (
        <p role="status" className="rounded-lg border border-green-200 bg-green-50 p-2.5 text-sm text-green-800">{aviso}</p>
      )}
      {error && <p role="alert" className="rounded-lg border border-red-200 bg-red-50 p-2.5 text-sm text-red-700">{error}</p>}

      {empezado && visible > 1 && (
        <button type="button" onClick={() => { setPaso(visible - 1); setError(null); setAviso(null); }}
                className="min-h-11 rounded-lg px-3 py-2 text-sm text-gray-700 hover:bg-gray-100">
          Paso anterior
        </button>
      )}
    </Marco>
  );
}

function Marco({ children, onSalir }) {
  return (
    <div className="min-h-screen bg-gray-50 px-4 py-6 sm:py-12">
      <div className="mx-auto max-w-lg space-y-5 rounded-2xl border border-gray-200 bg-white p-5 sm:p-7">
        <div className="flex items-start gap-3">
          <ShieldCheck className="h-7 w-7 shrink-0 text-[var(--color-marca)]" aria-hidden="true" />
          <div>
            <h1 className="text-xl font-semibold text-gray-900">Verifica tu empresa</h1>
            <p className="text-sm text-gray-600">Antes de empezar, TEGO comprueba que tu empresa y tú son quienes dicen ser.</p>
          </div>
        </div>
        <Ayuda id="seguridad.identidad.pantalla" />
        {children}
        {onSalir && (
          <div className="border-t border-gray-100 pt-3">
            <button type="button" onClick={() => onSalir()}
                    className="inline-flex min-h-11 items-center gap-2 rounded-lg px-3 py-2 text-sm text-gray-600 hover:bg-gray-100">
              <LogOut className="h-4 w-4" aria-hidden="true" /> Salir y seguir después
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
